<template>
  <div
    class="el-select"
    :class="[selectSize ? 'el-select--' + selectSize : '']"
    @click.stop="toggleMenu"
    v-clickoutside="handleClose"
  >
    <div
      class="el-select__tags"
      v-if="multiple"
      ref="tags"
      :style="{ 'max-width': inputWidth - 32 + 'px', width: '100%' }"
    >
      <span v-if="collapseTags && selected.length">
        <el-tag
          :closable="!selectDisabled"
          :size="collapseTagSize"
          :hit="selected[0].hitState"
          type="info"
          @close="deleteTag($event, selected[0])"
          disable-transitions
        >
          <slot
            name="tag"
            :item="selected[0]"
          >
            <span class="el-select__tags-text">{{ item.currentLabel }}</span>
          </slot>
        </el-tag>
        <el-tag
          v-if="selected.length > 1"
          :closable="false"
          :size="collapseTagSize"
          type="info"
          disable-transitions
        >
          <span class="el-select__tags-text">+ {{ selected.length - 1 }}</span>
        </el-tag>
      </span>
      <transition-group
        @after-leave="resetInputHeight"
        v-if="!collapseTags "
      >
        <el-tag
          v-for="item in selected"
          :key="getValueKey(item)"
          :closable="!selectDisabled"
          :size="collapseTagSize"
          :hit="item.hitState"
          type="info"
          @close="deleteTag($event, item)"
          disable-transitions
        >
          <slot
            name="tag"
            :item="item"
          >
            <span
              class="el-select__tags-text"
            >
              {{ item.currentLabel }}
            </span>
          </slot>
        </el-tag>
      </transition-group>

      <input
        type="text"
        class="el-select__input"
        :class="[selectSize ? `is-${ selectSize }` : '']"
        :disabled="selectDisabled"
        :autocomplete="autoComplete || autocomplete"
        @focus="handleFocus"
        @blur="softFocus = false"
        @keyup="managePlaceholder"
        @keydown="resetInputState"
        @keydown.down.prevent="handleNavigate('next')"
        @keydown.up.prevent="handleNavigate('prev')"
        @keydown.enter.prevent="selectOption"
        @keydown.esc.stop.prevent="visible = false"
        @keydown.delete="deletePrevTag"
        @keydown.tab="visible = false"
        @compositionstart="handleComposition"
        @compositionupdate="handleComposition"
        @compositionend="handleComposition"
        v-model="query"
        @input="debouncedQueryChange"
        v-if="filterable"
        :style="{ 'flex-grow': '1', width: inputLength / (inputWidth - 32) + '%', 'max-width': inputWidth - 42 + 'px' }"
        ref="input"
      >
    </div>
    <el-input
      ref="reference"
      v-model="selectedLabel"
      type="text"
      :placeholder="currentPlaceholder"
      :name="name"
      :id="id"
      :autocomplete="autoComplete || autocomplete"
      :size="selectSize"
      :disabled="selectDisabled"
      :readonly="readonly"
      :validate-event="false"
      :class="{ 'is-focus': visible }"
      :tabindex="(multiple && filterable) ? '-1' : null"
      @focus="handleFocus"
      @blur="handleBlur"
      @input="debouncedOnInputChange"
      @keydown.native.down.stop.prevent="handleNavigate('next')"
      @keydown.native.up.stop.prevent="handleNavigate('prev')"
      @keydown.native.enter.prevent="selectOption"
      @keydown.native.esc.stop.prevent="visible = false"
      @keydown.native.tab="visible = false"
      @compositionstart="handleComposition"
      @compositionupdate="handleComposition"
      @compositionend="handleComposition"
      @mouseenter.native="inputHovering = true"
      @mouseleave.native="inputHovering = false"
    >
      <template
        slot="prefix"
        v-if="$slots.prefix"
      >
        <slot name="prefix" />
      </template>
      <template slot="suffix">
        <i
          v-show="!showClose"
          :class="['el-select__caret', 'el-input__icon', 'el-icon-' + iconClass]"
        />
        <i
          v-if="showClose"
          class="el-select__caret el-input__icon el-icon-circle-close"
          @click="handleClearClick"
        />
      </template>
    </el-input>
    <transition
      name="el-zoom-in-top"
      @before-enter="handleMenuEnter"
      @after-leave="doDestroy"
    >
      <el-select-menu
        ref="popper"
        :append-to-body="popperAppendToBody"
        v-show="visible && emptyText !== false"
      >
        <el-scrollbar
          tag="ul"
          wrap-class="el-select-dropdown__wrap"
          view-class="el-select-dropdown__list"
          ref="scrollbar"
          :class="{ 'is-empty': !allowCreate && query && filteredOptionsCount === 0 }"
          v-show="options.length > 0 && !loading"
        >
          <el-option
            :value="query"
            created
            v-if="showNewOption"
          />
          <slot />
        </el-scrollbar>
        <template v-if="emptyText && (!allowCreate || loading || (allowCreate && options.length === 0 ))">
          <slot
            name="empty"
            v-if="$slots.empty"
          />
          <p
            class="el-select-dropdown__empty"
            v-else
          >
            {{ emptyText }}
          </p>
        </template>
      </el-select-menu>
    </transition>
  </div>
</template>

<script>
import { Select } from 'element-ui';

export default {
  extends: Select,
  computed: {
    bgcolor() {
      return 'transparent';
    },
    carretcolor() {
      return '#097FB5';
    },
    iconClass() {
      return this.visible ? 'arrow-up is-reverse' : 'arrow-up';
    },
  },
  methods: {
    handleFocus(event) {
      if (!this.softFocus) {
        if (this.automaticDropdown || this.filterable) {
          if (this.filterable && !this.visible) {
            this.menuVisibleOnFocus = true;
          }
          this.visible = true;
        }
        this.$emit('focus', event);
        this.dispatch('ElFormItem', 'el.form.input.focus');
      } else {
        this.softFocus = false;
      }
    },
    handleBlur(event) {
      setTimeout(() => {
        if (this.isSilentBlur) {
          this.isSilentBlur = false;
        } else {
          this.$emit('blur', event);
          this.dispatch('ElFormItem', 'el.form.input.blur');
        }
      }, 50);
      this.softFocus = false;
    },
  },
};
</script>

<style lang="scss">
.el-select {
  display: block;

  .el-tag--info {
    color: $--color-primary;
    border-color: $--color-gray-2;
    background-color: $--color-gray-7;

    .el-tag__close {
      color: $--color-primary;
    }
  }
  .el-input {
    .el-select__caret {
      color: v-bind(carretcolor);
    }
  }

  .el-input__inner {
    padding-right: rem(25px);
    font-weight: 500;
  }

  .el-input__suffix {
    width: rem(32px);
    right: 0;
    background-color: v-bind(bgcolor);
    border-radius: 0 4px 4px 0;

    .el-input__icon {
      font-weight: 700;
    }
  }
}

.el-select-dropdown {
  max-width: 100%;

  &.el-popper {
    margin: 0;
  }

  .popper__arrow {
    display: none;
  }
}

.el-select-dropdown__item {
  font-size: rem(13px);
  padding: rem(6px);
  line-height: 1;
  color: $--color-text-regular;

  &:hover,
  &.hover {
    color: $--color-white;
  }

  &.selected {
    background-color: $--select-option-selected;
    color: $--color-text-regular;
  }

  &.selected.hover {
    background-color: $--color-primary;
    color: $--color-white;
  }
}

.el-select-dropdown.is-multiple {
  .el-select-dropdown__item {
    &.selected.hover {
      color: $--color-white;
      background-color: $--color-primary;
    }
  }
}
</style>

<style lang="scss" scoped>
.dropdown-steady {
  position: relative;

  .el-select-dropdown {
    position: absolute !important;
    top: 100% !important;
    left: 0 !important;
    transform-origin: top !important;
  }
}

.dropdown-steady--with-prefix,
.select-v2--with-prefix {
  :deep() {
    .el-select-dropdown__item {
      display: flex;
      align-items: center;
      gap: 3px;
    }

    .el-input__prefix {
      display: inline-flex;
      align-items: center;
      width: auto;
      padding: 0 rem(7px);
    }

    .el-input__inner {
      padding-left: rem(56px);
    }
  }
}

.select-v2--with-prefix {
  :deep() {
    .el-input__prefix {
      padding-left: rem(9px);
    }
  }
}

.select-v2 {
  position: relative;

  .el-select-dropdown {
    position: absolute !important;
    top: 100% !important;
    left: 0 !important;
    transform-origin: top !important;
  }

  .el-select-dropdown,
  &.el-select-dropdown {
    border-color: $blue-mist;
    border-radius: 8px;
    border-width: 1px;
  }

  :deep() {
    .el-input__suffix {
      background-color: transparent;

      .el-input__icon {
        color: $--color-primary;
      }
    }

    .el-scrollbar__bar {
      display: none;
    }

    .el-input__inner {
      font-weight: 600;
    }

    .el-select-dropdown__item {
      font-size: 1rem;
      padding: 8px 9px;
      margin: 7px;
      border-radius: 8px;

      &:hover,
      &.hover {
        background-color: $--color-primary;
        font-weight: 600;
      }

      &.selected {
        background-color: #f4f9fb;
        font-weight: 600;
      }

      &.selected.hover {
        background-color: $--color-primary;
        color: $--color-white;
      }
    }

    .el-input.is-disabled {
      .el-input__inner:disabled {
        cursor: not-allowed;
        color: $--color-text-blue;
        -webkit-text-fill-color: $--color-text-blue;
      }

      .el-input__suffix .el-input__icon {
        color: $--color-text-blue;
      }
    }
  }
}

.select-v2--ellipsis {
  :deep(.el-input__inner) {
    text-overflow: ellipsis;
    overflow: hidden !important;
    white-space: nowrap;
  }
}

.el-select--date {
  .date__wrapper {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .date__text {
    color: $--color-text-blue;
    font-size: 14px;
  }

  .calendar-icon {
    align-self: center;
    color: $--color-primary;
    font-size: 24px;
    height: 24px;
    line-height: 24px;
    vertical-align: middle;
  }
}

.el-select--custom-label {
  :deep() {
    .el-input-group__prepend {
      background-color: $--color-white;
      border-radius: 8px;
      border: 1px solid $--border-color-base;
      color: $--color-text-primary;
      cursor: pointer;
      display: block;
      font-weight: 600;
      padding-left: 12px;
      padding-right: 25px;
      width: 100%;
    }
  }

  &:hover,
  &:focus,
  & .el-input.is-focus {
    :deep() {
      .el-input-group__prepend {
        border-color: $--color-primary;
      }
    }
  }

  .el-select__custom-label {
    display: flex;
    align-items: normal;
    gap: 4px;
    line-height: 38px;
    max-height: 38px;
    user-select: none;
  }

  &.el-select--medium {
    :deep() {
      .el-input-group__prepend {
        padding: 3.5px 28px 3.5px 12px;
      }
    }
  }
  &.el-select--large {
    :deep() {
      .el-input-group__prepend {
        padding: 8px 30px 8px 12px;
      }
    }
  }
}

.select--multiple {
  :deep() {
    .el-select__tags {
      margin-left: 10px;

      .el-select__input {
        margin-left: 0;
      }
    }

    .el-tag.el-tag--info {
      align-items: center;
      background-color: $--color-background;
      border-radius: 8px;
      border: none;
      display: inline-flex;
      gap: 4px;
      margin: 2px;
      min-height: 28px;
      max-width: calc(100% - 50px);
      padding: 4px 8px;
      height: auto;
    }

    .el-select__tags-text {
      color: $--color-primary;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.4;
    }

    .el-tag__close {
      right: -1px;
      font-weight: 800;
      color: $--color-white;
      background-color: $--color-primary;

      &:hover {
        background-color: $--color-primary-dark-3;
      }
    }

    .el-select-dropdown__item.selected::after {
      content: unset;
    }
  }
}

.select--advanced {
  width: 320px;
  min-width: 320px !important;

  :deep() {
    .el-select-dropdown__list {
      padding: 4px 0;
    }

    .el-select-dropdown__item {
      margin: 8px 12px;
      padding: 4px;
      color: $--color-text-regular;

      &:hover,
      &.hover,
      &.selected.hover,
      &.selected:hover {
        background-color: $--color-background;
        color: initial;
        font-weight: initial;
        cursor: auto;
      }

      &.selected {
        background-color: transparent;
        color: initial;
        font-weight: initial;
      }
    }
  }
}
</style>
